// src/components/patient/overview/types.ts
// Overview v3: the track type is the dashboard DTO; the v1 certification-period types that only the
// removed WorkflowWidget used are gone (plan docs/features/patient-overview-v3, F8).

import { disciplineCodes, disciplineLabel, getDisciplineColors } from '../../../constants/disciplines';
import type { ServiceTrackDto, TrackDisplayPhase } from '../../../dtos/patientmr/certificationPeriod.dto';

export type { Discipline, TrackPhase, PatientState, PatientSubstate, NextAction } from '../../../dtos/patientmr/certificationPeriod.dto';
export type { SOCChecklist } from '../../../dtos/patientmr/certificationPeriod.dto';

/** A discipline service track as the dashboard returns it */
export type ServiceTrack = ServiceTrackDto;

/** Display config for a discipline's color scheme. Uses full Tailwind class strings (JIT-safe). */
export interface DisciplineColorConfig {
  /** Light bg + dark text — for selected tab background (matches system pill style) */
  bg:     string;
  text:   string;
  /** Light bg — for tab hover/inactive backgrounds */
  light:  string;
  /** Border color */
  border: string;
  /** Phase badge pill (light bg + colored text) */
  pill:   string;
  /** Progress bar segment fill */
  bar:    string;
  /** Dark text on light bg — for labels over light backgrounds */
  label:  string;
  /** 500 / 700 borders for the unselected and selected tab, stronger bar for the selected tab */
  midBorder: string;
  strongBorder: string;
  barStrong: string;
}

/**
 * Discipline colours and labels (Phase 20A, D-12): from the discipline registry and the one shared
 * palette in constants/disciplines.ts, so the Overview, schedule and pills agree. `pill` includes
 * the border class.
 */
export const DISCIPLINE_COLORS: Record<string, DisciplineColorConfig> = Object.fromEntries(
  disciplineCodes({ includePlanned: true }).map(code => {
    const c = getDisciplineColors(code);
    return [code, { bg: c.bgColor, text: c.textColor, light: c.light, border: c.borderColor, pill: c.pill, bar: c.bar, label: c.label, midBorder: c.midBorder, strongBorder: c.strongBorder, barStrong: c.barStrong }];
  })
);

/** Canonical display order for discipline tabs (registry order) */
export const DISCIPLINE_ORDER = disciplineCodes() as ServiceTrack['discipline'][];

/** Human-readable discipline display names (registry labels) */
export const DISCIPLINE_DISPLAY: Record<string, string> = Object.fromEntries(
  disciplineCodes({ includePlanned: true }).map(code => [code, disciplineLabel(code)])
);

/**
 * Display phase of a track. Newer responses send it; older ones (and NOT_ORDERED placeholders) are
 * derived from the stored phase. PA_EXPIRING / PA_EXPIRED are "in care" with a PA flag (D1).
 */
export function trackDisplayPhase(track: Pick<ServiceTrack, 'phase' | 'displayPhase'>): TrackDisplayPhase | 'NOT_ORDERED' {
  if (track.displayPhase) return track.displayPhase;
  switch (track.phase) {
    case 'NOT_ORDERED':        return 'NOT_ORDERED';
    case 'SOC_IN_PROGRESS':    return 'ONBOARDING';
    case 'DISCHARGED':         return 'DISCHARGED';
    default:                   return 'IN_CARE';
  }
}

export function trackPaFlag(track: Pick<ServiceTrack, 'phase' | 'paFlag'>): 'EXPIRING' | 'EXPIRED' | null {
  if (track.paFlag !== undefined) return track.paFlag;
  if (track.phase === 'PA_EXPIRED') return 'EXPIRED';
  if (track.phase === 'PA_EXPIRING') return 'EXPIRING';
  return null;
}

/** Tab badge per display phase */
export const PHASE_BADGE: Record<TrackDisplayPhase | 'NOT_ORDERED', { label: string; className: string }> = {
  NOT_ORDERED: { label: 'Not ordered', className: 'bg-slate-100 text-slate-500' },
  ONBOARDING:  { label: 'Onboarding',  className: 'bg-blue-100 text-blue-700' },
  IN_CARE:     { label: 'In care',     className: 'bg-emerald-100 text-emerald-700' },
  DISCHARGED:  { label: 'Discharged',  className: 'bg-slate-100 text-slate-500' },
};

/**
 * Substate → short label (track tabs). Discipline-prefixed substates (RN_CARE_TEAM_UNASSIGNED,
 * PA_EXPIRING_PT) are resolved by getSubstateLabel below.
 */
export const SUBSTATE_LABEL: Record<string, string> = {
  NO_EPISODE:              'No referral yet',
  REFERRAL_PENDING:        'Referral needed',
  DEMOGRAPHICS_INCOMPLETE: 'Demographics needed',
  INSURANCE_UNVERIFIED:    'Insurance needed',
  CARE_TEAM_UNASSIGNED:    'Provider needed',
  ASSESSMENT_PENDING:      'Assessment needed',
  CARE_PLAN_PENDING:       'Care plan needed',
  PHYSICIAN_ORDER_PENDING: 'Signed order needed',
  CONSENT_PENDING:         'Consent needed',
  DME_PENDING:             'Equipment needed',
  EMERGENCY_PLAN_PENDING:  'Emergency plan needed',
  HHAX_SETUP_PENDING:      'HHAX setup needed',
  PA_PENDING:              'PA needed',
  READY:                   'Ready to start',
  READY_TO_ADMIT:          'Ready to admit',
  READY_TO_ACTIVATE:       'Ready to start',
  ACTIVE_IN_CARE:          'In care',
  ON_HOLD:                 'On hold',
  NO_ACTIVE_TRACKS:        'No active services',
  DISCHARGE_IN_PROGRESS:   'Discharge tasks open',
  BILLING_PENDING:         'Billing open',
  CLOSE_READY:             'Ready to close',
  CASE_CLOSED:             'Closed',
};

const DISCIPLINE_PATTERN = disciplineCodes({ includePlanned: true }).join('|');

/**
 * Resolve a substate label, handling discipline-prefixed substates.
 * 'RN_CARE_TEAM_UNASSIGNED' → 'Provider needed' (the tab already names the discipline) or, with
 * `withDiscipline`, 'RN — Provider needed'. 'PA_EXPIRING_PT' → 'PA ends soon'.
 */
export function getSubstateLabel(substate: string, withDiscipline = false): string {
  if (SUBSTATE_LABEL[substate]) return SUBSTATE_LABEL[substate];
  const paMatch = substate.match(new RegExp(`^(PA_EXPIRING|PA_EXPIRED)_(${DISCIPLINE_PATTERN})$`));
  if (paMatch) {
    const label = paMatch[1] === 'PA_EXPIRING' ? 'PA ends soon' : 'PA ended';
    return withDiscipline ? `${paMatch[2]} — ${label}` : label;
  }
  const discMatch = substate.match(new RegExp(`^(${DISCIPLINE_PATTERN})_(.+)$`));
  if (discMatch && SUBSTATE_LABEL[discMatch[2]]) {
    return withDiscipline ? `${discMatch[1]} — ${SUBSTATE_LABEL[discMatch[2]]}` : SUBSTATE_LABEL[discMatch[2]];
  }
  return substate;
}
